import { Box, Table } from '@chakra-ui/react';
import type React from 'react';
import FaceliftText from '@/components/Atoms/FaceliftText/FaceliftText';
import Show from '@/components/Molecules/Show';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import type { ChartWithTableViewProps } from './ChartWithTableView.types';

const ChartWithTableView: React.FC<ChartWithTableViewProps> = ({
	children,
	title,
	tableData,
	showMenu = false,
	isTableModalOpen = false,
	setIsTableModalOpen,
}) => {
	const { colorMode } = useColorMode();
	const isDark = colorMode === 'dark';

	return (
		<Box position="relative" width="100%" height="100%">
			{/* Original Chart Content */}
			{children}

		{/* Table View Modal - Positioned inside chart container with padding to preserve borders */}
		<Show when={isTableModalOpen}>
				<Box
					position="absolute"
					top="3px"
					left="3px"
					right="3px"
					bottom="3px"
					bg="rgba(0, 0, 0, 0.6)"
					backdropFilter="blur(4px)"
					display="flex"
					alignItems="center"
					justifyContent="center"
					zIndex={10}
					borderRadius="md"
					onClick={() => setIsTableModalOpen?.(false)}
				>
					<Box
						width="90%"
						maxWidth="700px"
						maxH="85%"
						bg={{ base: 'white', _dark: colors.tabelCellDark }}
						borderRadius="xl"
						boxShadow="0px 4px 4px 0px #00000040"
						overflow="hidden"
						onClick={(e) => e.stopPropagation()}
						display="flex"
						flexDirection="column"
					>
						{/* Modal Header */}
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							px={6}
							py={4}
							borderBottom="1px solid"
							borderColor={isDark ? 'gray.700' : 'gray.200'}
							_dark={{ bg: colors.tabelCellDark }}
							_light={{ bg: 'white' }}
						>
							<FaceliftText fontSize="x-large" fontWeight={600}>
								{title ? `${title}` : 'Chart - Table View'}
							</FaceliftText>
							<Box
								cursor="pointer"
								onClick={() => setIsTableModalOpen?.(false)}
								display="flex"
								alignItems="center"
								justifyContent="center"
								width={8}
								height={8}
								borderRadius="full"
								bg="linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%)"
								color="white"
								transition="opacity 0.2s"
								_hover={{
									opacity: 0.9,
								}}
								_active={{
									transform: 'scale(0.98)',
								}}
							>
								<FaceliftText fontSize="lg" fontWeight={700} lineHeight={1}>
									✕
								</FaceliftText>
							</Box>
						</Box>

						{/* Modal Body with Clean Table */}
						<Box
							flex={1}
							overflowY="auto"
							px={6}
							pt={4}
							pb={6}
							_dark={{ bg: colors.tabelCellDark }}
							_light={{ bg: 'white' }}
						>
							<Table.Root size="md" variant="outline" striped>
								<Table.Header>
									<Table.Row
										_dark={{ bg: 'gray.800' }}
										_light={{ bg: 'gray.100' }}
									>
										{tableData.columns.map((column) => (
											<Table.ColumnHeader 
												key={column.key}
												fontWeight={600}
												fontSize="md"
												py={3}
												px={4}
												textAlign={column.align || 'left'}
												_dark={{ color: 'gray.200' }}
												_light={{ color: 'gray.700' }}
												borderBottom="1px solid"
												borderColor={isDark ? 'gray.700' : 'gray.300'}
											>
												{column.label}
											</Table.ColumnHeader>
										))}
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Show when={tableData.rows.length > 0}>
										{tableData.rows.map((row, index) => (
											<Table.Row key={index}>
												{tableData.columns.map((column) => (
													<Table.Cell 
														key={column.key}
														fontWeight={400}
														fontSize="sm"
														py={3}
														px={4}
														textAlign={column.align || 'left'}
														_dark={{ 
															color: 'gray.300',
															borderColor: 'gray.700'
														}}
														_light={{ 
															color: 'gray.600',
															borderColor: 'gray.200'
														}}
													>
														{column.render ? column.render(row[column.key], row) : row[column.key]}
													</Table.Cell>
												))}
											</Table.Row>
										))}
									</Show>
									<Show when={tableData.rows.length === 0}>
										<Table.Row>
											<Table.Cell colSpan={tableData.columns.length} textAlign="center" py={12}>
												<FaceliftText
													fontSize="md"
													fontWeight={500}
													_dark={{ color: 'gray.400' }}
													_light={{ color: 'gray.500' }}
												>
													No data available
												</FaceliftText>
											</Table.Cell>
										</Table.Row>
									</Show>
								</Table.Body>
							</Table.Root>
						</Box>
					</Box>
				</Box>
			</Show>
		</Box>
	);
};

export default ChartWithTableView;

